'use client';

import type { HomeDonationBusinessDto } from '__generated__/api/unauth';
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import { ProjectCard } from '../../_components/ProjectCard';
import { SPECIFIC_DONATION_STATUS } from '../../constants';
import type { HometownBusinessSearchParams } from '../page';

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 5;

interface HometownBusinessListProps {
  initialList: HomeDonationBusinessDto[];
  totalCount: number;
  searchParams: HometownBusinessSearchParams;
}

export const HometownBusinessList = ({
  initialList,
  totalCount,
  searchParams,
}: HometownBusinessListProps) => {
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(
      UnauthInfiniteQueryOptions['get:/wello/v2/allows/home/donation-business'](
        {
          query: {
            ...(searchParams ?? {}),
            page: INITIAL_PAGE,
            size: INITIAL_SIZE,
          },
        },
      ),
    ),
    placeholderData: {
      pageParams: [INITIAL_PAGE],
      pages: [
        {
          context: {
            contents: initialList,
            totalCount: totalCount,
            page: INITIAL_PAGE,
            size: INITIAL_SIZE,
          },
        },
      ],
    },
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (!isFetching && inView) fetchNextPage();
    },
  });

  const list =
    data?.pages?.flatMap((page) => page?.context?.contents ?? []) ?? [];
  const isEmpty = totalCount < 1;

  const handleClick = (item: HomeDonationBusinessDto) => {
    trackGtmEvent(GTM_EVENT_TYPE.SELECT_ITEM, {
      currency: 'KRW',
      local_name: item.institutionName,
      value: item.donationAmount,
      items: [
        {
          item_type: '고향사업',
          item_list_name: '고향사업',
          item_list_id: item.donationBusinessId.toString(),
          item_name: item.donationBusinessName,
          quantity: 1,
          item_id: item.donationBusinessId.toString(),
          price: item.donationAmount,
          business_name: item.donationBusinessName,
        },
      ],
    });
  };

  return (
    <>
      <ul
        className={cn(
          'grid grid-cols-3 gap-x-16 gap-y-24 pb-80',
          'tablet:grid-cols-2',
          'mobile:grid-cols-1',
        )}
      >
        {list.map((item) => {
          const {
            achievementRate: achievement_rate,
            donationAmount: donation_amount,
            dday,
            institutionLogoUrl: institution_logo_url,
            institutionName: institution_name,
            thumbnailUrl: thumbnail_url,
            donationBusinessName: donation_business_name,
            donationBusinessId: donation_business_id,
            institutionId: institution_id,
            institutionSlugId,
            specificDonationStatus,
            donationBusinessSlugId,
          } = item;

          return (
            <li key={donation_business_id}>
              <ProjectCard
                achievementRate={achievement_rate}
                donationAmount={donation_amount}
                daysLeft={dday}
                institution={{
                  id: institutionSlugId ?? institution_id,
                  logoUrl: institution_logo_url,
                  name: institution_name,
                }}
                thumbnailUrl={thumbnail_url}
                title={donation_business_name}
                businessId={donation_business_id}
                slugId={donationBusinessSlugId}
                isEnded={
                  specificDonationStatus === SPECIFIC_DONATION_STATUS.ENDED
                }
                onProjectLinkClick={() => handleClick(item)}
              />
            </li>
          );
        })}
        {isEmpty && (
          <li
            className={cn(
              'h-full pt-150 text-center font-400 text-Gray-550 flex-center typo-body2',
              'mobile-tablet:typo-body3',
            )}
            style={{ gridColumn: '1 / -1' }}
          >
            아직 등록된 지정기부가 없어요.
            <br />
            지역에서 지정기부를 등록하면, 웰로에서 보실 수 있어요.
          </li>
        )}
      </ul>
      {hasNextPage ? (
        <div className="my-30 w-full flex-center" ref={ref}>
          <Spinner />
        </div>
      ) : null}
    </>
  );
};
